import type { AccessState, UserAccessSnapshot } from '@/features/commandDeck/contracts/types'

/** Access states that may enter app product interiors (not Command Deck production). */
export const APP_INTERIOR_GRANT_ACCESS_STATES: readonly AccessState[] = [
  'creator_beta',
  'studio_access',
  'founder_mode',
  'admin_mode',
] as const

/** Pathway-only states — holding UX, no product interior (B3). */
export const PATHWAY_ACCESS_STATES: readonly AccessState[] = [
  'requested_access',
  'demo_booked',
  'enterprise_review',
  'investor_preview',
] as const

export function hasAppInteriorGrant(
  access: Pick<UserAccessSnapshot, 'accessState' | 'accountReadiness'>
): boolean {
  return (APP_INTERIOR_GRANT_ACCESS_STATES as readonly string[]).includes(access.accessState)
}

export function isPathwayOnlyAccessState(accessState: AccessState): boolean {
  return (PATHWAY_ACCESS_STATES as readonly string[]).includes(accessState)
}

/** Primary B3 pathway shell states (authenticated, no product interior). */
export function shouldUseDedicatedPathwayShell(
  access: Pick<UserAccessSnapshot, 'accessState'>
): boolean {
  return access.accessState === 'requested_access' || access.accessState === 'demo_booked'
}

export function isPathwayOnlyAccount(
  access: Pick<UserAccessSnapshot, 'accessState' | 'accountReadiness'>
): boolean {
  return (
    shouldUseDedicatedPathwayShell(access) ||
    access.accountReadiness === 'pathway_only' ||
    (isPathwayOnlyAccessState(access.accessState) && !hasAppInteriorGrant(access))
  )
}

/**
 * Authenticated users who must not see product interiors (fail-closed).
 */
export function shouldHoldAuthenticatedUser(
  access: Pick<UserAccessSnapshot, 'accessState' | 'accountReadiness'>
): boolean {
  if (hasAppInteriorGrant(access)) return false
  if (access.accessState === 'visitor') return true
  if (isPathwayOnlyAccessState(access.accessState)) return true
  return access.accountReadiness === 'not_ready' || access.accountReadiness === 'pathway_only'
}

export type ControlledAccessHoldReason = 'signed_out' | 'no_grant' | 'session_error'

export function resolveHoldReason(
  status: 'anonymous' | 'authenticated',
  access: UserAccessSnapshot,
  sessionError: string | null
): ControlledAccessHoldReason {
  if (status === 'anonymous') {
    return sessionError ? 'session_error' : 'signed_out'
  }
  return sessionError ? 'session_error' : 'no_grant'
}
